import AnalyticsHelper from '../../utils/analytics-helper';

export default class Navigation {
    constructor(parent) {
        this.parent = parent;
        this.hashManager = parent.hashManager;
        this.el = parent.el.querySelector('.nav-content-wrapper');
        this.data = parent.data;
        this.car = parent.car;
        this.pricing = parent.pricing;
        this.finance = parent.finance;
        this.navButton = this.el.querySelector('.nav-links-current');
        this.navLinks = this.el.querySelectorAll('.nav-links ol li a');
        this.sections = parent.el.querySelectorAll('[data-section]');
        this.priceLoader = this.el.querySelector('.nav-price-loader');
        this.configuratorVisualiser = this.parent.el.querySelector('.configurator--visualiser');
        this.configuratorNav = this.parent.el.querySelector('.configurator--nav');
        this.isFocusTrapActive = false;
        this.onFocusTrapKeydown = null;
        this.observed = {};
        this.callbacks = [];
        this.scrolled = false;
        this.init();
        this.bind();
    }

    setMenuExpanded(isExpanded) {
        if (!this.navButton) return;
        this.navButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }

    activateFocusTrap() {
        if (this.isFocusTrapActive || !this.navButton) {
            return;
        }

        this.isFocusTrapActive = true;

        this.onFocusTrapKeydown = (e) => {
            if (!this.navButton?.classList?.contains('open-menu')) {
                return;
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                this.closeMenu(true);
                return;
            }

            if (e.key !== 'Tab') {
                return;
            }

            const focusables = [this.navButton, ...Array.from(this.navLinks || [])].filter(Boolean);
            if (!focusables.length) {
                return;
            }

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;

            if (e.shiftKey) {
                if (active === first || !this.el.contains(active)) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (active === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', this.onFocusTrapKeydown);
    }

    deactivateFocusTrap() {
        if (!this.isFocusTrapActive) {
            return;
        }

        this.isFocusTrapActive = false;
        if (this.onFocusTrapKeydown) {
            document.removeEventListener('keydown', this.onFocusTrapKeydown);
        }
        this.onFocusTrapKeydown = null;
    }

    closeMenu(shouldFocusButton = true) {
        if (!this.navButton.classList.contains('open-menu')) {
            return;
        }

        this.navButton.classList.remove('open-menu');
        this.setMenuExpanded(false);
        this.deactivateFocusTrap();
        if (shouldFocusButton) {
            this.navButton.focus();
        }
    }

    init() {
        this.analyticsPushOnLoad();
    }

    async scrollOnLoad() {
        if (!this.scrolled) {
            const sectionId = this.hashManager.get('section');
            if (sectionId) {
                this.scrollToSection(`#${sectionId}`)
                this.hashManager.remove('section');
                this.hashManager.update();
                this.scrolled = true;
            }
        }
    }

    bind() {
        if (this.navButton) {
            this.navButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.navButton.classList.toggle('open-menu');
                const isOpen = this.navButton.classList.contains('open-menu');
                this.setMenuExpanded(isOpen);
                if (isOpen) {
                    this.activateFocusTrap();
                } else {
                    this.deactivateFocusTrap();
                    this.navButton.focus();
                }
            });
        }

        this.navLinks.forEach((element) => {
            element.addEventListener('click', (e) => {
                this.handleClickLink(e);
            });
        });

        this.setCuePointObserver();
    }

    addEventListener(cb) {
        this.callbacks.push(cb);
    }

    static isMobile() {
        return window.matchMedia('(max-width: 1024px)').matches;
    }

    handleClickLink(event) {
        event.preventDefault();
        const list = event.target.closest('ol') || event.target.closest('ul');
        if (list) {
            list.querySelectorAll('li').forEach((el) => {
                el.classList.remove('active');
            });
        }

        event.target.closest('li').classList.add('active');
        const link = event.target.closest('a[href]');
        const target = link ? link.getAttribute('href') : null;

        if (target) {
            this.scrollToSection(target);
        } else {
            console.error('No valid section found');
        }

        this.closeMenu(false);
    }

    scrollToSection(target) {
        this.parent.visualiser.forceScrollAction();

        const sectionsWithVisualiser = ['#grade-engine', '#exterior', '#interior'];
        const targetElement = document.querySelector(target);

        if (!targetElement?.firstElementChild) return;

        const bodyTop = document.body.getBoundingClientRect().top;
        const targetTop = targetElement.getBoundingClientRect().top;
        const hasVisualiser = Navigation.isMobile() && sectionsWithVisualiser.includes(target);
        const carCanvasHeight = hasVisualiser
            ? this.configuratorVisualiser?.getBoundingClientRect().height || 0
            : 0;

        const navHeight = this.configuratorNav?.getBoundingClientRect().height || 0;
        const offset = carCanvasHeight + navHeight;

        window.scrollTo({
            top: targetTop - bodyTop - offset,
            behavior: 'smooth',
        });

        // Accessibility: set focus selected .configurator--divider element
        const focusTarget = targetElement.querySelector('.configurator--divider') ?? targetElement.querySelector('.steps-header');;
        if (focusTarget) {
            focusTarget.tabIndex = -1;
            focusTarget.focus({ preventScroll: true });
        }
    }

    setCuePointObserver() {
        const options = {
            rootMargin: '0px',
            threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95, 1],
        };

        const callback = (entries) => {
            const sectionArr = Array.from(this.sections);
            for (const entry of entries) {
                entry.target._intersectionHeight = entry.intersectionRect.height;
                if(entry.target._intersectionHeight > 0) {
                    this.observed[entry.target.dataset.section] = true;
                }
            }

            const mostVisibleSection = sectionArr.reduce((prev, current) => {
                if (current._intersectionHeight > (prev ? prev._intersectionHeight : 0)) {
                    return current;
                }
                return prev;
            }, null);

            const prevMostVisibleLink = this.el.querySelector('.nav-links-wrapper li.active');

            if (!mostVisibleSection) {
                prevMostVisibleLink && prevMostVisibleLink.classList.remove('active');
                return;
            }

            const mostVisibleLink = this.el.querySelector(`.${mostVisibleSection.dataset.section}-tab`);

            if (mostVisibleLink && mostVisibleLink !== prevMostVisibleLink) {
                prevMostVisibleLink && prevMostVisibleLink.classList.remove('active');
                mostVisibleLink.classList.add('active', 'tick');
                this.sectionId = mostVisibleSection?.dataset.section;
                this.sectionName = mostVisibleLink.querySelector('a').innerText.trim();
                this.navButton.innerText = this.sectionName;

                const isExterior = this.sectionId === 'grade-engine' || this.sectionId === 'exterior';
                const isInterior = this.sectionId === 'interior';

                if (isExterior && this.parent.visualiser.view !== 'exterior') {
                    this.parent.visualiser.exterior();
                } else if (isInterior && this.parent.visualiser.view !== 'interior') {
                    this.parent.visualiser.interior();
                }

                AnalyticsHelper.push(mostVisibleLink.querySelector('a'));

                if (this.sectionId === 'summary') {
                    this.analyticsPushSummaryView();
                }

                this.parent?.visualiser?.toggle(this.sectionId);
                this.callbacks.forEach((cb) => cb(this.sectionId));
            }
        };

        const observer = new IntersectionObserver(callback, options);

        this.sections.forEach((section) => {
            observer.observe(section);
        });
    }

    hasObserved(section) {
        return !!this.observed[section];
    }

    showLoader() {
        this.priceLoader.classList.remove('hide');
    }

    hideLoader() {
        this.priceLoader.classList.add('hide');
    }

    analyticsPushSummaryView() {
        const documentCountryCode = document.querySelector('html').getAttribute('country');
        const model = this.data?.model.modelBodyCode;
        const totalPrice = this.pricing?.getCarTotalPrice();
        const depositPrice = this.pricing?.getCarHoldingFee();
        const mtoce = this.car?.mtoce();
        const selectedGrade = this.car?.grade;
        const selectedColour = this.car?.colour;

        AnalyticsHelper.push({ ecommerce: null }); // Clear the previous ecommerce object.
        AnalyticsHelper.push({
            event: 'add_to_cart',
            actionType: 'ecommerce - add to cart',
            businessUnit: 'cars',
            moduleName: 'ecommerce',
            clickText: '',
            clickType: '',
            destinationURL: '',
            modelSelected: model,
            inPageLocation: 'bottom',
            countryCode: documentCountryCode,
            ecommerce: {
                currency: window.HONDA?.currency?.ISO,
                value: totalPrice,
                items: [
                    {
                        item_id: mtoce,
                        item_name: model,
                        discount: 0,
                        index: 0,
                        item_brand: 'HONDA',
                        gradeName: selectedGrade,
                        colourName: selectedColour,
                        dealerName: '',
                        price: totalPrice,
                        quantity: 1,
                    },
                ],
            },
        });
        AnalyticsHelper.push({
            event: 'configurator_steps_complete_mtoce',
            eventCategory: 'Car Configurator',
            eventAction: 'Your Summary - mtoce',
            eventLabel: `cars| ${model} | ${mtoce} | ${selectedGrade} | ${selectedColour} | configurator | Step 7. Summary | Summary | internal | bottom | #`,
            actionType: 'Your Summary - mtoce',
            businessUnit: 'cars',
            moduleName: 'configurator',
            clickText: 'Summary',
            clickType: 'internal',
            destinationURL: '#',
            configuratorStepName: 'Step 7. Summary',
            modelSelected: model,
            inPageLocation: 'bottom',
            gradeName: selectedGrade,
            colourName: selectedColour,
            mtoce: `${mtoce}`,
            depositAmount: depositPrice,
        });
    }

    analyticsPushOnLoad() {
        const activeSection = this.el.querySelector('.nav-links-wrapper li.active');
        AnalyticsHelper.push(activeSection.querySelector('a'));
    }
}
